import { type ConfigSection, type GameProfile, type Menu, MenuItem, type ProfileCtx } from "./types";
import defaultProfile, { asyncRoute } from "@/game_profiles/default";
import { commonMenuItems } from "@/game_profiles/common";
import { i18n } from "@/i18n";
import {
  faClockRotateLeft,
  faFileImport,
  faLink,
  faLinkHorizontal,
  faPuzzlePiece,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { computed, markRaw } from "vue";
import type { DayZProfileNamespace, SteamWorkshopItem } from "@/graphql";
import DialogConfirm from "@/components/dialogs/DialogConfirm.vue";
import { useDialogStore } from "@/store/dialog";
import { useServiceStore } from "@/store/service";
import { usePropertyBuilder } from "@/components/views/config/helpers";
import { useSteamWorkshop } from "@/components/views/mods/steam_workshop";
import type { IUseMods, ModTag } from "@/components/views/mods";

const { t } = i18n.global;

const configSections: ConfigSection[] = [
  { component: "ConfSection", props: { name: "basic" } },
  { component: "ConfSection", props: { name: "serverRules" } },
  { component: "ConfSection", props: { name: "extended" } },
  { component: "ConfSection", props: { name: "playerAndWorld" } },
  { component: "ConfSection", props: { name: "baseBuilding" } },
  { component: "ConfSection", props: { name: "economy" } },
  { component: "ConfSection", props: { name: "whitelistSettings" } },
  { component: "TableSection", props: { name: "whitelist" } },
  { component: "TableSection", props: { name: "motd" } },
  { component: "BroadcastMessages" },
];

const consoleCommands = ["players", "say", "kick", "ban", "bans", "removeBan"];

const createMenu = (): Menu => {
  const menu = defaultProfile().menu;
  menu[0].items.push(
    new MenuItem(
      "save-backups",
      faClockRotateLeft,
      asyncRoute(() => import("../components/views/dayZ/SaveBackups.vue")),
    ),
    new MenuItem(
      "save-import",
      faFileImport,
      asyncRoute(() => import("../components/views/dayZ/SaveImport.vue")),
    ),
    commonMenuItems.logs,
    commonMenuItems.console,
    new MenuItem(
      "mods",
      faPuzzlePiece,
      asyncRoute(() => import("../components/views/config/dayZ/Mods.vue")),
      [
        {
          route: "manage",
          component: asyncRoute(() => import("../components/views/config/dayZ/ModsManage.vue")),
        },
      ],
    ),
  );
  return menu;
};

// Most specific first: a mod tagged Vehicle and Mechanics is a vehicle mod.
const CATEGORY_PRIORITY = [
  "Terrain",
  "Vehicle",
  "Weapon",
  "Animation",
  "Character",
  "Equipment",
  "Props",
  "Economy",
  "Sound",
  "Environment",
  "Mechanics",
  "Server",
];

export function workshopCategory(mod: SteamWorkshopItem) {
  const tags = mod.dayz?.tags ?? [];
  return CATEGORY_PRIORITY.find((category) => tags.includes(category));
}

export function useMods() {
  const service = useServiceStore();
  const { buildProperty, buildConfig } = usePropertyBuilder();

  const profile = computed(() => service.ctx.profile as DayZProfileNamespace | undefined);
  const selectedWorkshopItems = computed(() => profile.value?.selectedWorkshopItems ?? []);
  const workshopModIds = buildProperty<Array<string>>("virtual", "workshopMods", []);
  const dependencyModIds = buildProperty<Array<string>>("virtual", "dependencyMods", []);
  const subscribedCollectionIds = buildConfig<Array<string>>("subscribedCollectionIds", []);
  const workshop = useSteamWorkshop(selectedWorkshopItems, workshopModIds, subscribedCollectionIds);
  const { installedMods } = workshop;

  const installedIds = computed(() => new Set(workshopModIds.value));
  const dependencyMods = computed(() => new Set(dependencyModIds.value));
  const foldersOnDisk = computed(() => new Set(profile.value?.directoryMods ?? []));

  function dependentsOf(workshopId: string) {
    return installedMods.value.filter((mod) => mod.dayz?.requiredItems.includes(workshopId));
  }

  const missingRequirements = computed(() => {
    const missing = new Map<string, SteamWorkshopItem[]>();
    for (const mod of installedMods.value) {
      for (const required of mod.dayz?.requiredItems ?? []) {
        if (!installedIds.value.has(required)) {
          missing.set(required, [...(missing.get(required) ?? []), mod]);
        }
      }
    }
    return missing;
  });

  const unusedDependencies = computed(() =>
    installedMods.value.filter(
      (mod) => dependencyMods.value.has(mod.id.toString()) && !dependentsOf(mod.id.toString()).length,
    ),
  );

  function joinNames(mods: SteamWorkshopItem[]) {
    return mods.map((mod) => mod.name).join(", ");
  }

  function modTags(mod: SteamWorkshopItem): ModTag[] {
    const workshopId = mod.id.toString();
    const tags: ModTag[] = [];
    if (installedIds.value.has(workshopId)) {
      tags.push(
        foldersOnDisk.value.has(`@${workshopId}`) ? t("ngp/dayZ/mods/tag/installed") : t("ngp/dayZ/mods/tag/pending"),
      );
      const dependents = dependentsOf(workshopId);
      if (dependents.length) {
        tags.push({
          label: t("ngp/dayZ/mods/tag/required_by", dependents.length),
          icon: faLink,
          tooltip: joinNames(dependents),
        });
      }
      if (dependencyMods.value.has(workshopId)) {
        tags.push({ label: t("ngp/dayZ/mods/tag/dependency"), icon: faLinkHorizontal });
      }
    } else if (mod.dayz?.requiredItems.length) {
      tags.push({ label: t("ngp/dayZ/mods/tag/requires", mod.dayz.requiredItems.length), icon: faLink });
    }
    const category = workshopCategory(mod);
    if (category) {
      tags.push(category);
    }
    return tags;
  }

  async function confirmRemoval(mod: SteamWorkshopItem) {
    const dependents = dependentsOf(mod.id.toString());
    if (!dependents.length) {
      return true;
    }
    return await useDialogStore().createDialog({
      component: markRaw(DialogConfirm),
      props: {
        headerIcon: faTriangleExclamation,
        headerText: t("ngp/dayZ/mods/remove/confirm_title"),
        contentText: t("ngp/dayZ/mods/remove/confirm_text", { mod: mod.name, dependents: joinNames(dependents) }),
      },
    });
  }

  async function removeMod(mod: SteamWorkshopItem) {
    if (await confirmRemoval(mod)) {
      workshop.removeMod(mod);
    }
  }

  function addDependency(mod: SteamWorkshopItem) {
    workshop.addMod(mod);
    if (!dependencyMods.value.has(mod.id.toString())) {
      dependencyModIds.value = [...dependencyModIds.value, mod.id.toString()];
    }
  }

  return {
    ...workshop,
    workshopModIds,
    modTags,
    removeMod,
    addDependency,
    confirmRemoval,
    missingRequirements,
    unusedDependencies,
  };
}

const join = () => {
  window.open(t("ngp/settings/section/dayz_wiki/link"), "_blank", "noopener,noreferrer");
};

export default (_ctx: ProfileCtx): Partial<GameProfile<SteamWorkshopItem, IUseMods<SteamWorkshopItem>>> => ({
  configSections,
  useMods,
  staleModWarning: false,
  menu: createMenu(),
  consoleCommands,
  join: {
    buttonTextKey: "ngp/settings/section/dayz_wiki",
    joinFn: join,
  },
});
